import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { getTechLocations, getCompanyLocations, updateCompanyAddress, fetchCompanyAddressFromAutoTask } from '../services/api';
import { TechLocation, CompanyLocation, MissingCompany } from '../services/types';
import { useAuth } from '../contexts/AuthProvider';
import 'leaflet/dist/leaflet.css';

// Tech marker (blue circle with person icon)
const techIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Company marker (green circle with building icon)
const companyIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#22c55e;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
      <path d="M9 22V12h6v10"/>
      <path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01"/>
    </svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

// Auto-fit bounds to include both tech and company locations
function FitBounds({ techLocations, companyLocations, showClients }: {
  techLocations: TechLocation[];
  companyLocations: CompanyLocation[];
  showClients: boolean;
}) {
  const map = useMap();

  useEffect(() => {
    const points: [number, number][] = [
      ...techLocations.map((l) => [l.latitude, l.longitude] as [number, number]),
      ...(showClients ? companyLocations.map((l) => [l.latitude, l.longitude] as [number, number]) : []),
    ];

    if (points.length === 0) return;

    if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [techLocations, companyLocations, showClients, map]);

  return null;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function FieldMap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<TechLocation[]>([]);
  const [companyLocations, setCompanyLocations] = useState<CompanyLocation[]>([]);
  const [missingAddresses, setMissingAddresses] = useState<MissingCompany[]>([]);
  const [showClients, setShowClients] = useState(true);
  const [showAllClients, setShowAllClients] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLocations = useCallback(async () => {
    try {
      const [techData, companyResponse] = await Promise.all([
        getTechLocations(),
        getCompanyLocations(undefined, undefined, showAllClients),
      ]);
      setLocations(techData);
      setCompanyLocations(companyResponse.data);
      setMissingAddresses(companyResponse.missingAddresses);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [showAllClients]);

  useEffect(() => {
    loadLocations();
    const interval = setInterval(loadLocations, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [loadLocations, showAllClients]);

  // Default center: Southern California (Temecula area)
  const defaultCenter: [number, number] = [33.4936, -117.1484];

  return (
    <div className="space-y-4">
      <PageHeader
        userEmail={user?.email}
        middleContent={
          <div className="text-center">
            <span className="text-sm font-medium text-white">Field Map</span>
            <span className="text-xs text-slate-500 ml-2">
              {locations.length} tech{locations.length !== 1 ? 's' : ''}
              {companyLocations.length > 0 && `, ${companyLocations.length} client${companyLocations.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        }
      />

      {error && (
        <div className="bg-red-900/30 border border-red-700/40 rounded-lg p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Missing address notification */}
      {missingAddresses.length > 0 && (
        <button
          onClick={() => setShowMissing(!showMissing)}
          className="w-full bg-amber-900/30 border border-amber-700/40 rounded-lg px-3 py-2 text-sm text-amber-300 hover:bg-amber-900/40 transition-colors text-left flex items-center justify-between"
        >
          <span>{missingAddresses.length} client{missingAddresses.length !== 1 ? 's' : ''} missing address data</span>
          <span className="text-xs text-amber-400">{showMissing ? 'Hide' : 'Add Addresses'}</span>
        </button>
      )}

      <div className="flex gap-4" style={{ height: showMissing ? 'calc(100vh - 180px)' : 'calc(100vh - 140px)' }}>
      {/* Missing address panel */}
      {showMissing && (
        <MissingAddressPanel
          companies={missingAddresses}
          onSaved={() => {
            loadLocations();
          }}
        />
      )}

      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden relative flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : (
          <MapContainer
            center={defaultCenter}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
            className="rounded-lg"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds
              techLocations={locations}
              companyLocations={companyLocations}
              showClients={showClients}
            />

            {/* Tech markers */}
            {locations.map((loc) => (
              <Marker
                key={`tech-${loc.resourceId}`}
                position={[loc.latitude, loc.longitude]}
                icon={techIcon}
              >
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold text-slate-900">{loc.resourceName}</div>
                    <div className="text-slate-600 text-xs mt-1">
                      Last updated: {formatTimeAgo(loc.updatedAt)}
                    </div>
                    {loc.accuracy && (
                      <div className="text-slate-500 text-xs">
                        Accuracy: ~{Math.round(loc.accuracy)}m
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Company markers (click to open popup with ticket link) */}
            {showClients && companyLocations.map((loc) => (
              <Marker
                key={`company-${loc.companyId}`}
                position={[loc.latitude, loc.longitude]}
                icon={companyIcon}
              >
                <Popup offset={[0, -14]}>
                  <div className="text-sm">
                    <div className="font-semibold text-slate-900">{loc.companyName}</div>
                    {loc.address && (
                      <div className="text-slate-600 text-xs mt-1">{loc.address}</div>
                    )}
                    {loc.openTicketCount > 0 ? (
                      <a
                        href={`/tickets?client=${encodeURIComponent(loc.companyName)}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/tickets?client=${encodeURIComponent(loc.companyName)}`);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs mt-1 block underline"
                      >
                        {loc.openTicketCount} open ticket{loc.openTicketCount !== 1 ? 's' : ''} &rarr;
                      </a>
                    ) : (
                      <div className="text-slate-500 text-xs mt-1">No open tickets</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {/* Legend & toggle overlay */}
        {!loading && (
          <div className="absolute bottom-4 left-4 z-[1000] bg-slate-800/90 backdrop-blur-sm rounded-lg border border-slate-600 p-3 text-xs space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 border border-white" />
              <span className="text-slate-300">Tech</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500 border border-white" />
              <span className="text-slate-300">Client</span>
              <button
                onClick={() => setShowClients(!showClients)}
                className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  showClients
                    ? 'bg-green-600/30 text-green-400 hover:bg-green-600/50'
                    : 'bg-slate-600/30 text-slate-500 hover:bg-slate-600/50'
                }`}
              >
                {showClients ? 'ON' : 'OFF'}
              </button>
            </div>
            {showClients && (
              <div className="flex items-center gap-2 pl-5">
                <button
                  onClick={() => setShowAllClients(!showAllClients)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    showAllClients
                      ? 'bg-green-600/30 text-green-400 hover:bg-green-600/50'
                      : 'bg-slate-600/30 text-slate-500 hover:bg-slate-600/50'
                  }`}
                >
                  {showAllClients ? 'All Clients' : 'Open Tickets Only'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      </div>{/* close flex container */}
    </div>
  );
}

// ── Missing Address Panel ──

function MissingAddressPanel({ companies, onSaved }: {
  companies: MissingCompany[];
  onSaved: () => void;
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ address1: '', city: '', state: '', postalCode: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  const [fetchingId, setFetchingId] = useState<number | null>(null);
  const [fetchError, setFetchError] = useState<{ id: number; msg: string } | null>(null);
  const [fetchAllLoading, setFetchAllLoading] = useState(false);

  const startEdit = (company: MissingCompany) => {
    setEditingId(company.companyId);
    setForm({ address1: '', city: '', state: '', postalCode: '' });
    setSaveError(null);
    setFetchError(null);
  };

  const handleSave = async (companyId: number) => {
    if (!form.address1 && !form.city && !form.postalCode) {
      setSaveError('Enter at least an address, city, or zip code');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await updateCompanyAddress(companyId, form);
      setSavedIds(prev => new Set(prev).add(companyId));
      setEditingId(null);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFetchFromAT = async (companyId: number) => {
    setFetchingId(companyId);
    setFetchError(null);
    try {
      await fetchCompanyAddressFromAutoTask(companyId);
      setSavedIds(prev => new Set(prev).add(companyId));
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Not found in AutoTask';
      setFetchError({ id: companyId, msg });
    } finally {
      setFetchingId(null);
    }
  };

  const handleFetchAll = async () => {
    setFetchAllLoading(true);
    setFetchError(null);
    const remaining = unsaved.filter(c => fetchingId !== c.companyId);
    let fetched = 0;
    for (const company of remaining) {
      try {
        await fetchCompanyAddressFromAutoTask(company.companyId);
        setSavedIds(prev => new Set(prev).add(company.companyId));
        fetched++;
      } catch {
        // Skip companies with no AT address
      }
    }
    setFetchAllLoading(false);
    if (fetched > 0) onSaved();
  };

  const unsaved = companies.filter(c => !savedIds.has(c.companyId));

  return (
    <div className="w-80 bg-slate-800 rounded-lg border border-slate-700 overflow-y-auto">
      <div className="p-3 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white">Missing Addresses ({unsaved.length})</h3>
          {unsaved.length > 0 && (
            <button
              onClick={handleFetchAll}
              disabled={fetchAllLoading}
              className="text-[10px] bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white px-2 py-0.5 rounded transition-colors"
            >
              {fetchAllLoading ? 'Fetching...' : 'Fetch All from AT'}
            </button>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-1">Fetch from AutoTask or add manually</p>
      </div>
      <div className="divide-y divide-slate-700">
        {unsaved.map(company => (
          <div key={company.companyId} className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white truncate" title={company.companyName}>
                {company.companyName}
              </span>
              {editingId !== company.companyId && (
                <div className="flex gap-1 ml-2 shrink-0">
                  <button
                    onClick={() => handleFetchFromAT(company.companyId)}
                    disabled={fetchingId === company.companyId}
                    className="text-xs text-emerald-400 hover:text-emerald-300 disabled:text-emerald-700"
                    title="Fetch address from AutoTask"
                  >
                    {fetchingId === company.companyId ? '...' : 'AT'}
                  </button>
                  <span className="text-slate-600">|</span>
                  <button
                    onClick={() => startEdit(company)}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    Manual
                  </button>
                </div>
              )}
            </div>
            {fetchError?.id === company.companyId && (
              <div className="text-[10px] text-red-400 mt-1">{fetchError.msg}</div>
            )}
            {company.geocodeError && !fetchError?.id && (
              <div className="text-[10px] text-red-400 mt-1">Geocode error: {company.geocodeError}</div>
            )}
            {editingId === company.companyId && (
              <div className="mt-2 space-y-2">
                <input
                  type="text"
                  placeholder="Street address"
                  value={form.address1}
                  onChange={e => setForm(f => ({ ...f, address1: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-400"
                />
                <div className="flex gap-1">
                  <input
                    type="text"
                    placeholder="City"
                    value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={form.state}
                    onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    className="w-12 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-400"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Zip code"
                  value={form.postalCode}
                  onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-400"
                />
                {saveError && <div className="text-[10px] text-red-400">{saveError}</div>}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(company.companyId)}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white text-xs py-1 rounded transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-2 bg-slate-600 hover:bg-slate-500 text-white text-xs py-1 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {unsaved.length === 0 && (
          <div className="p-3 text-xs text-slate-400 text-center">All addresses resolved!</div>
        )}
      </div>
    </div>
  );
}
